import { ReactElement, useState } from 'react';
import { Grid, Box, List, Divider, Collapse, Button, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { opacities } from 'styles/vars';
import { GVTypography } from 'components/lib';

interface GVCollapsibleListProps {
  renderList: ReactElement[];
  onCollapse?: () => void;
  visibleCount?: number;
}

const useStyles = makeStyles()((theme: Theme) => {
  return {
    table: {
      flex: '1',
      height: '100%',
      overflowY: 'auto',
    },
    showAction: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      paddingLeft: theme.spacing(0.5),
      opacity: opacities.superhigh,
      gap: theme.spacing(0.4),
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    divider: {
      backgroundColor: '#fff',
      opacity: 0.08,
      width: `calc(100% - calc(${theme.spacing(6)}))`, // minus left + right margin
      margin: `0 ${theme.spacing(3)}`,
    },
    buttonText: {
      fontSize: '12px',
    },
    chevron: {
      fontSize: '16px',
    },
  };
});

const GVCollapsibleList = ({ renderList, onCollapse, visibleCount = 5 }: GVCollapsibleListProps) => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const handleShowAll = (): void => {
    if (expanded && onCollapse) {
      onCollapse();
    }
    setExpanded(!expanded);
  };

  if (!visibleCount) {
    return null;
  }

  return (
    <Grid item container direction="column" wrap="nowrap" className={classes.table}>
      <List dense>
        <Divider className={classes.divider} component="li" />
        {renderList.slice(0, visibleCount)}
        <Collapse in={expanded}>{renderList.slice(visibleCount)}</Collapse>
      </List>
      {renderList.length > visibleCount && (
        <Box justifyContent="center" alignItems="flex-start" display="flex" width="100%">
          <Button className={classes.showAction} onClick={handleShowAll}>
            {expanded ? (
              <>
                <KeyboardArrowUpIcon className={classes.chevron} />
                <GVTypography
                  className={classes.buttonText}
                  variant="body1"
                  color="textPrimary"
                  fontWeight={600}
                  emphasis="superhigh"
                >
                  Hide All
                </GVTypography>
              </>
            ) : (
              <>
                <KeyboardArrowDownIcon className={classes.chevron} />
                <GVTypography
                  className={classes.buttonText}
                  variant="body1"
                  color="textPrimary"
                  fontWeight={600}
                  emphasis="superhigh"
                >
                  Show All
                </GVTypography>
              </>
            )}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default GVCollapsibleList;
